<template>
    <div ref="containerRef" class="object-cover w-full h-full">
        <div v-if="isVisible" class="flex items-center justify-center object-cover w-full h-full fade-enter-active"
            :class="{ 'fade-enter': !loaded, 'fade-enter-to': loaded }">
            <slot name="image" :load="onLoad" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";

// `eager` marks an above-the-fold / LCP image: it renders during SSR (so the
// real <img> is in the server HTML and the preload scanner can find it) and
// skips the opacity-0 fade-in — both of which otherwise disqualify the image
// as a Largest Contentful Paint candidate. Leave it off for below-the-fold
// images so they stay lazy-loaded via the IntersectionObserver.
const { eager = false } = defineProps<{ eager?: boolean }>();

const loaded = ref(eager);
const isVisible = ref(eager);
const containerRef = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

const onLoad = function () {
    loaded.value = true;
};

onMounted(() => {
    // Eager images are already visible and painted; no observer needed.
    if (eager) return;

    if (containerRef.value) {
        // Use Intersection Observer to detect when component enters viewport
        observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !isVisible.value) {
                        isVisible.value = true;

                        // Check if image is already cached after rendering
                        setTimeout(() => {
                            if (containerRef.value && !loaded.value) {
                                const img = containerRef.value.querySelector('img');
                                if (img && img.complete && img.naturalHeight !== 0) {
                                    loaded.value = true;
                                }
                            }
                        }, 0);
                    }
                });
            },
            {
                rootMargin: '50px', // Start loading 50px before entering viewport
            }
        );

        observer.observe(containerRef.value);
    }
});

onUnmounted(() => {
    if (observer && containerRef.value) {
        observer.unobserve(containerRef.value);
        observer.disconnect();
    }
});
</script>

<style scoped lang="scss">
.fade-enter-active {
    transition: opacity 0.5s ease-in-out;
}

.fade-enter-to {
    opacity: 1;
}

.fade-enter {
    opacity: 0;
}
</style>
